import { Flex, Text } from '@chakra-ui/react';
import { ConfirmModal, type ConfirmModalHandle } from '@shared';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
	IoBagCheckOutline,
	IoBusinessOutline,
	IoCartOutline,
	IoHomeOutline,
	IoLogOutOutline,
	IoMailOutline,
	IoMegaphoneOutline,
	IoPeopleOutline,
	IoPhonePortraitOutline,
	IoSendOutline,
} from 'react-icons/io5';
import { MdOutlineAdminPanelSettings } from 'react-icons/md';
import { useNavigate } from 'react-router';
import { useSuperadminAuth } from '../../context/SuperadminAuthContext';

export default function MobileMenu({ isMobileMenuOpen, setIsMobileMenuOpen }: { isMobileMenuOpen: boolean; setIsMobileMenuOpen: (value: boolean) => void }) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { clearAuthData } = useSuperadminAuth();

	const sidebarIconSize = 30;
	const menuItems = [
		{
			icon: <IoHomeOutline color="white" size={sidebarIconSize} />,
			label: t('general.overview'),
			onClick: () => {
				navigate(`/`);
				setIsMobileMenuOpen(false);
			},
		},
		{
			icon: <IoMailOutline color="white" size={sidebarIconSize} />,
			label: t('general.sms_messages'),
			onClick: () => {
				navigate(`/sms-messages`);
				setIsMobileMenuOpen(false);
			},
		},
		{
			icon: <IoBusinessOutline color="white" size={sidebarIconSize} />,
			label: t('general.accounts'),
			onClick: () => {
				navigate(`/accounts`);
				setIsMobileMenuOpen(false);
			},
		},
		{
			icon: <IoPeopleOutline color="white" size={sidebarIconSize} />,
			label: t('general.users'),
			onClick: () => {
				navigate(`/users`);
				setIsMobileMenuOpen(false);
			},
		},
		{
			icon: <MdOutlineAdminPanelSettings color="white" size={sidebarIconSize} />,
			label: t('general.superadmins'),
			onClick: () => {
				navigate(`/superadmins`);
				setIsMobileMenuOpen(false);
			},
		},
		{
			icon: <IoCartOutline color="white" size={sidebarIconSize} />,
			label: t('general.orders'),
			onClick: () => {
				navigate(`/orders`);
				setIsMobileMenuOpen(false);
			},
		},
		{
			icon: <IoBagCheckOutline color="white" size={sidebarIconSize} />,
			label: t('general.discount_codes'),
			onClick: () => {
				navigate(`/discount-codes`);
				setIsMobileMenuOpen(false);
			},
		},
		{
			icon: <IoMegaphoneOutline color="white" size={sidebarIconSize} />,
			label: t('general.announcements'),
			onClick: () => {
				navigate(`/announcements`);
				setIsMobileMenuOpen(false);
			},
		},
		{
			icon: <IoSendOutline color="white" size={sidebarIconSize} />,
			label: t('general.sender_names'),
			onClick: () => {
				navigate(`/sender-names`);
				setIsMobileMenuOpen(false);
			},
		},
		{
			icon: <IoPhonePortraitOutline color="white" size={sidebarIconSize} />,
			label: t('general.inbound_numbers'),
			onClick: () => {
				navigate(`/inbound-numbers`);
				setIsMobileMenuOpen(false);
			},
		},
	];

	async function handleLogout() {
		const response = await waitForUserResponseOnLogOut.current?.open({
			title: t('general.log_out'),
			content: t('general.really_log_out'),
			confirmButtonText: t('general.log_out'),
			confirmButtonVariant: 'subtle',
		});
		if (response === false) {
			return;
		}
		clearAuthData();
	}

	const waitForUserResponseOnLogOut = useRef<ConfirmModalHandle>(null);

	return (
		<Flex
			transition="transform 0.5s ease"
			top="60px"
			transform={isMobileMenuOpen ? 'translateY(0)' : 'translateY(-100%)'}
			pointerEvents={isMobileMenuOpen ? 'auto' : 'none'}
			borderRadius="0px 0px 0px 50px"
			boxShadow={isMobileMenuOpen ? '0px 4px 4px 0px rgba(0, 0, 0, 0.25)' : ''}
			zIndex={1}
			flexDirection="column"
			backgroundColor="rgba(61, 213, 152, 1)"
			width="100%"
			position="absolute"
			left="0"
		>
			{menuItems.map(item => (
				<MenuItem key={item.label} icon={item.icon} label={item.label} actionOnClick={item.onClick} />
			))}
			<MenuItem icon={<IoLogOutOutline color="white" size={sidebarIconSize} />} label={t('general.log_out')} actionOnClick={handleLogout} />
			<ConfirmModal ref={waitForUserResponseOnLogOut} />
		</Flex>
	);
}

function MenuItem({ icon, label, actionOnClick }: { icon: React.ReactNode; label: string; actionOnClick?: () => void }) {
	const sidebarItemFontSize = 13;

	return (
		<Flex
			fontSize={sidebarItemFontSize}
			padding={2}
			cursor={'pointer'}
			flexDirection={'column'}
			gap={2}
			width={'100%'}
			minHeight={'45px'}
			onClick={actionOnClick}
			alignItems={'center'}
			justifyContent={'center'}
		>
			<span>{icon}</span>
			<Text color="white" wordBreak={'break-word'}>
				{label}
			</Text>
		</Flex>
	);
}
